import { Card, CardContent, Stack, Typography } from "@mui/material";
import { FeaturePermission } from "@/__generated__/featurePermissions";
import { MoxieCardType } from "@/__generated__/schema.graphql.types";
import { useUser } from "@/auth/useUser";
import { CrownIcon } from "@/components/common/icons";
import Dropdown from "@/components/dropdown/dropdown";
import { GREY } from "@/config/mui/colorPalette";
import { ClientMembershipsQuery } from "@/graphql/queries/clientMemberships.graphql.types";
import useClientMembership from "@/hooks/client/useClientMembership";
import { MembershipStatus } from "@/types";
import { MembershipClientCardContent } from "./membershipClientCardContent";

type Props = {
  membership: ClientMembershipsQuery["clientMembership"][number];
  clientId: string;
  defaultCard?: MoxieCardType;
  isClientUser?: boolean;
};

export default function MembershipClientCard({
  membership,
  clientId,
  defaultCard,
  isClientUser,
}: Props) {
  const { newPermissionsEnabledForUser, hasFeaturePermission } = useUser();
  const {
    cancelMembership,
    changeMembershipCreditCard,
    viewMembershipAgreement,
  } = useClientMembership(membership, clientId);

  const membershipNotCanceled =
    membership.status !== MembershipStatus.CANCELED &&
    membership.cancelAt === null;

  const canCancelMembership =
    !isClientUser &&
    membershipNotCanceled &&
    (newPermissionsEnabledForUser
      ? hasFeaturePermission(FeaturePermission.MANAGE_CLIENT_MEMBERSHIP)
      : true);
  const canChangeCreditCard =
    !isClientUser &&
    membershipNotCanceled &&
    (newPermissionsEnabledForUser
      ? hasFeaturePermission(FeaturePermission.VIEW_CLIENT_WALLET)
      : true);
  const canViewMembership = newPermissionsEnabledForUser
    ? hasFeaturePermission(FeaturePermission.MANAGE_CLIENT_MEMBERSHIP)
    : true;

  const dropdownActions = [
    canCancelMembership && {
      title: "Cancel membership",
      action: cancelMembership,
    },
    (canViewMembership || isClientUser) && {
      title: "View membership agreement",
      action: viewMembershipAgreement,
    },
    canChangeCreditCard && {
      title: "Change credit card",
      action: changeMembershipCreditCard,
    },
  ].filter(Boolean);

  return (
    <Card sx={{ overflow: "visible", pt: 0, width: "100%" }}>
      <CardContent sx={{ p: 0 }}>
        <Stack
          direction="row"
          sx={{
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <MembershipTitle title={membership.membership.title} />
          {dropdownActions.length > 0 && (
            <Dropdown additionalActions={dropdownActions} />
          )}
        </Stack>
        <MembershipClientCardContent
          membership={membership}
          defaultCard={defaultCard}
        />
      </CardContent>
    </Card>
  );
}

const MembershipTitle = ({ title }: { title: string }) => (
  <Typography
    variant="subtitleDefault"
    component="span"
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1,
      flexGrow: 1,
      wordBreak: "break-all",
    }}
  >
    <CrownIcon color={GREY[70]} size="24px" />
    {title}
  </Typography>
);
